import React, { useEffect, useState } from "react";
import { Loader2, FileText, Check, X } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { useJob, JobProgress } from "./WorkbenchJobs";

const Draft = ({ p, onDone }) => {
  const [text, setText] = useState(p.text);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const words = text.trim().split(/\s+/).length;
  const decide = async (accept) => {
    setBusy(true);
    try {
      await api.post("/admin/curios/workbench/expand/decide", { slug: p.slug, accept, text });
      toast.success(accept ? `Published longer article (${words} words)` : "Draft discarded");
      onDone(accept);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); setBusy(false); }
  };
  return (
    <div className="border-t border-[#E9E1D0] py-3" data-testid={`expand-draft-${p.slug}`}>
      <div className="flex items-center justify-between gap-3">
        <button onClick={() => setOpen((o) => !o)} className="text-left min-w-0">
          <p className="font-serif-display text-base font-semibold text-[#1A1615] truncate">{p.title}</p>
          <p className="font-mono-cv text-[10px] text-[#7A6C5D]">{p.from_words} → {words} words · grounded on {p.grounded_on?.length || 0} Wikipedia page(s) · {open ? "hide" : "review"}</p>
        </button>
        <div className="flex gap-2 shrink-0">
          <button data-testid={`expand-approve-${p.slug}`} disabled={busy} onClick={() => decide(true)} className="inline-flex items-center gap-1 text-xs font-semibold text-[#FAF7F0] bg-[#2F6B33] px-2.5 py-1 rounded-sm disabled:opacity-50"><Check className="w-3.5 h-3.5" /> Approve</button>
          <button data-testid={`expand-reject-${p.slug}`} disabled={busy} onClick={() => decide(false)} className="inline-flex items-center gap-1 text-xs font-semibold text-[#8A1C1C] border border-[#E2B7B2] px-2.5 py-1 rounded-sm disabled:opacity-50"><X className="w-3.5 h-3.5" /> Reject</button>
        </div>
      </div>
      {open && (
        <div className="grid md:grid-cols-2 gap-3 mt-2">
          <div>
            <p className="font-mono-cv text-[10px] uppercase text-[#7A6C5D] mb-1">Current</p>
            <p className="font-ui text-xs text-[#4A4036] whitespace-pre-line bg-[#FAF7F0] border border-[#E9E1D0] rounded-sm p-2 max-h-72 overflow-y-auto">{p.current}</p>
          </div>
          <div>
            <p className="font-mono-cv text-[10px] uppercase text-[#7A6C5D] mb-1">Proposed (editable)</p>
            <textarea data-testid={`expand-text-${p.slug}`} value={text} onChange={(e) => setText(e.target.value)} rows={14} className="w-full font-ui text-xs text-[#1A1615] border border-[#D8CEB9] rounded-sm p-2 bg-white" />
          </div>
        </div>
      )}
    </div>
  );
};

export const ExpandPanel = ({ onChange }) => {
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/curios/workbench/expand/list").then((r) => setData(r.data)).catch(() => {});
  const { job, start, running } = useJob((j) => { toast.success(`Drafted ${j.done - j.failed} longer articles`); load(); });
  useEffect(() => { load(); }, []);
  if (!data) return null;
  const pending = data.short_count - data.proposals.length;
  return (
    <div className="bg-white border border-[#D8CEB9] rounded-sm p-4 mb-4" data-testid="expand-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="font-serif-display text-lg font-bold text-[#1A1615]">Longer Articles</p>
          <p className="font-ui text-xs text-[#7A6C5D]"><b data-testid="expand-short-count">{data.short_count}</b> curios are under 200 words. Gemini drafts 260–380 words using only the curio's Wikipedia sources; nothing changes until you approve.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="expand-propose-btn" disabled={running || pending <= 0} onClick={() => start("/admin/curios/workbench/expand/propose").catch(() => toast.error("Could not start"))}
            className="inline-flex items-center gap-1.5 bg-[#9E4535] text-[#FAF7F0] font-ui text-xs font-semibold px-3 py-2 rounded-sm hover:bg-[#82382B] disabled:opacity-50">
            {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />} Draft longer versions ({Math.max(pending, 0)})
          </button>
          <JobProgress job={job} testid="expand-progress" />
        </div>
      </div>
      {data.proposals.length > 0 && (
        <div className="mt-3 max-h-[560px] overflow-y-auto pr-1" data-testid="expand-drafts-list">
          <p className="font-mono-cv text-[10px] uppercase tracking-wider text-[#7A6C5D] mb-1">{data.proposals.length} drafts awaiting review</p>
          {data.proposals.map((p) => <Draft key={p.slug} p={p} onDone={(acc) => { load(); if (acc) onChange(); }} />)}
        </div>
      )}
    </div>
  );
};
